/**
 * `entities/taxonomy-field-template` mutations. Every write invalidates the
 * library list; toasts surface here for consistent feedback.
 */
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { i18n } from '@/shared/i18n';
import { enqueueSnackbar } from 'notistack';
import { assetsHttp } from '@/lib/api/http-client';
import { notificationService } from '@/lib/services/notification.service';
// The mirror of the deviation documented in `taxonomy-category/api/mutations.ts`,
// and load-bearing for the same reason: `TaxonomyCategory.fieldCount` is counted
// live per request, so any template write changes every category's count. Only
// this slice knows a template was written, and the delete confirmation reads
// `fieldCount` off the cached row to tell the user what a delete will re-file.
// eslint-disable-next-line no-restricted-syntax -- see above; revisit if a third slice needs this
import { TAXONOMY_CATEGORY_QUERY_KEYS } from '@/entities/taxonomy-category';
import type {
  CreateTaxonomyFieldTemplatePayload,
  TaxonomyFieldTemplate,
  UpdateTaxonomyFieldTemplatePayload,
} from '../model/types';
import { TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS } from './queries';

const TEMPLATES_URL = '/api/v1/taxonomy-field-templates';

function invalidateLibrary(queryClient: QueryClient) {
  // Invalidate the whole slice, not just the list: every write also appends
  // to the template's audit trail, and a narrower key left the history
  // drawer showing "No changes recorded yet" straight after an edit.
  void queryClient.invalidateQueries({ queryKey: TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS.all });
  void queryClient.invalidateQueries({ queryKey: TAXONOMY_CATEGORY_QUERY_KEYS.all });
}

export function useCreateFieldTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTaxonomyFieldTemplatePayload) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: TEMPLATES_URL,
        body: payload,
      });
      if (!response.data) throw new Error('Failed to create field');
      return response.data as TaxonomyFieldTemplate;
    },
    onSuccess: () => {
      invalidateLibrary(queryClient);
      enqueueSnackbar(
        i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.fieldAddedLibrary'),
        { variant: 'success' },
      );
    },
    onError: () =>
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.failedAddField'), {
        variant: 'error',
      }),
  });
}

/**
 * Bulk create — seeds the library from the standard-fields catalog the frontend
 * already ships, so the defaults have a single source of truth.
 */
export function useCreateFieldTemplatesBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templates: CreateTaxonomyFieldTemplatePayload[]) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${TEMPLATES_URL}/bulk`,
        body: { templates },
      });
      return (response.data as TaxonomyFieldTemplate[] | undefined) ?? [];
    },
    onSuccess: (created) => {
      invalidateLibrary(queryClient);
      enqueueSnackbar(
        i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.addedStandardFields', {
          count: created.length,
        }),
        { variant: 'success' },
      );
    },
    onError: () =>
      enqueueSnackbar(
        i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.failedAddStandardFields'),
        { variant: 'error' },
      ),
  });
}

export function useUpdateFieldTemplate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateTaxonomyFieldTemplatePayload) => {
      const response = await assetsHttp.patch({
        throwOnError: true,
        url: `${TEMPLATES_URL}/{id}`,
        path: { id },
        body: payload,
      });
      return response.data as TaxonomyFieldTemplate;
    },
    onSuccess: () => {
      invalidateLibrary(queryClient);
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.fieldUpdated'), {
        variant: 'success',
      });
    },
    onError: () =>
      enqueueSnackbar(
        i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.failedUpdateField'),
        { variant: 'error' },
      ),
  });
}

type RemovedFieldTemplate = Pick<TaxonomyFieldTemplate, 'id' | 'name'>;

// `removedIds` is absent from an API that predates it; `deletedIds` is the closest it has.
const bulkDeleteFieldTemplatesResultSchema = z
  .object({ deletedIds: z.array(z.string()), removedIds: z.array(z.string()).optional() })
  .transform((result) => result.removedIds ?? result.deletedIds);

const restoreFieldTemplatesResultSchema = z.object({
  restoredIds: z.array(z.string()),
  blocked: z.array(z.object({ id: z.string(), name: z.string(), code: z.string() })),
});
type RestoreFieldTemplatesResult = z.infer<typeof restoreFieldTemplatesResultSchema>;

function announceRestore(removed: RemovedFieldTemplate[], result: RestoreFieldTemplatesResult) {
  const restored = result.restoredIds.length;
  const blocked = result.blocked.length;

  if (restored === 0 && blocked === 0) {
    notificationService.showInfo(
      i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.nothingRestored'),
    );
  } else if (removed.length === 1 && blocked > 0) {
    notificationService.showWarning(
      i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.restoreBlockedNamed', {
        name: removed[0].name,
      }),
    );
  } else if (removed.length === 1) {
    notificationService.showSuccess(
      i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.restoredFieldNamed', {
        name: removed[0].name,
      }),
    );
  } else if (restored === 0) {
    notificationService.showWarning(
      i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.restoreBlockedFields', {
        count: blocked,
      }),
    );
  } else if (blocked > 0) {
    notificationService.showWarning(
      i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.restoredFieldsBlocked', {
        restored: i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.restoredFields', {
          count: restored,
        }),
        count: blocked,
      }),
    );
  } else {
    notificationService.showSuccess(
      i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.restoredFields', {
        count: restored,
      }),
    );
  }
}

async function restoreFieldTemplates(queryClient: QueryClient, removed: RemovedFieldTemplate[]) {
  let result: RestoreFieldTemplatesResult;
  try {
    const response = await assetsHttp.post({
      throwOnError: true,
      url: `${TEMPLATES_URL}/actions/restore`,
      body: { ids: removed.map((tpl) => tpl.id) },
    });
    result = restoreFieldTemplatesResultSchema.parse(response.data);
  } catch {
    notificationService.showError(
      i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.failedRestore', {
        count: removed.length,
      }),
    );
    return;
  } finally {
    // Also after a failure: the server restores row by row, so some may be back.
    invalidateLibrary(queryClient);
  }
  announceRestore(removed, result);
}

/**
 * The removal toast. Closing it starts an exit animation that leaves the button
 * clickable for a moment, so the latch is what keeps a double click to one restore.
 */
function announceRemoval(
  queryClient: QueryClient,
  message: string,
  removed: RemovedFieldTemplate[],
) {
  let sent = false;
  const key = notificationService.showSuccess(message, {
    action: {
      label: i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.undo'),
      onClick: () => {
        if (sent) return;
        sent = true;
        notificationService.close(key);
        void restoreFieldTemplates(queryClient, removed);
      },
    },
  });
}

export function useDeleteFieldTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: RemovedFieldTemplate) => {
      await assetsHttp.delete({
        throwOnError: true,
        url: `${TEMPLATES_URL}/{id}`,
        path: { id: template.id },
      });
      return template;
    },
    onSuccess: (template) => {
      invalidateLibrary(queryClient);
      announceRemoval(
        queryClient,
        i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.removedFieldNamed', {
          name: template.name,
        }),
        [template],
      );
    },
    onError: () =>
      notificationService.showError(
        i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.failedRemoveField'),
      ),
  });
}

/**
 * Removes several templates at once. There is no in-use refusal, unlike lists,
 * since nothing binds TO a template. The server skips ids the caller does not
 * own, so the toast and its Undo cover only the rows it reports as removed.
 */
export function useBulkDeleteFieldTemplates(options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templates: RemovedFieldTemplate[]) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${TEMPLATES_URL}/bulk-delete`,
        body: { ids: templates.map((tpl) => tpl.id) },
      });
      const removedIds = new Set(bulkDeleteFieldTemplatesResultSchema.parse(response.data));
      return templates.filter((tpl) => removedIds.has(tpl.id));
    },
    onSuccess: (removed) => {
      invalidateLibrary(queryClient);
      if (removed.length === 0) {
        notificationService.showInfo(
          i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.nothingRemoved'),
        );
      } else {
        announceRemoval(
          queryClient,
          i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.removedFields', {
            count: removed.length,
          }),
          removed,
        );
      }
      options?.onSuccess?.();
    },
    onError: () =>
      notificationService.showError(
        i18n.t('knowledge:taxonomy.taxonomyFieldTemplateMutations.failedRemoveFields'),
      ),
  });
}
